/**
 * Currency utility functions for dynamic currency formatting
 * Based on hotel's configured currency setting
 */

// Default currency fallback
const DEFAULT_CURRENCY = 'INR';
const DEFAULT_LOCALE = 'en-IN';

// Currency to locale mapping for better formatting
const CURRENCY_LOCALE_MAP = {
  'INR': 'en-IN',
  'USD': 'en-US',
  'EUR': 'en-EU',
  'GBP': 'en-GB',
  'JPY': 'ja-JP',
  'AUD': 'en-AU',
  'CAD': 'en-CA',
  'CHF': 'de-CH',
  'CNY': 'zh-CN',
  'JOD': 'en-JO',
  'IDR': 'id-ID',
  'MYR': 'ms-MY',
  'DOP': 'es-DO',
  'AED': 'en-AE',
  'SGD': 'en-SG',
  'LAK': 'lo-LA',
  'THB': 'th-TH',
  'TRY': 'tr-TR',
  'MXN': 'es-MX',
  'VND': 'vi-VN',
  'SAR': 'ar-SA',
  'BRL': 'pt-BR',
  'PHP': 'en-PH',
  'MVR': 'dv-MV',
  'ZAR': 'en-ZA',
  'HKD': 'zh-HK',
  'KES': 'en-KE',
  'NGN': 'en-NG',
  'GHS': 'en-GH',
  'LKR': 'si-LK',
  'NPR': 'ne-NP',
  'MAD': 'en-MA',
  'EGP': 'en-EG',
  'TZS': 'en-TZ',
  'QAR': 'en-QA',
  'BDT': 'en-BD',
  'PKR': 'en-PK',
  'NZD': 'en-NZ',
  'COP': 'es-CO',
  'PEN': 'es-PE'
};

// Currency symbols for quick reference
const CURRENCY_SYMBOLS = {
  'INR': '₹',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'AUD': 'A$',
  'CAD': 'C$',
  'CHF': 'CHF',
  'CNY': '¥',
  // Jordanian Dinar — officially 3dp (fils), but the backend stores all
  // money at 2dp, so we display 2dp to match the ledger.
  'JOD': 'JD',
  'IDR': 'Rp',
  'MYR': 'RM',
  'DOP': 'RD$',
  'AED': 'د.إ',
  'SGD': 'S$',
  'LAK': '₭',
  'THB': '฿',
  'TRY': '₺',
  'MXN': 'Mex$',
  'VND': '₫',
  'SAR': 'SR',
  'BRL': 'R$',
  'PHP': '₱',
  'MVR': 'Rf',
  'ZAR': 'R',
  'HKD': 'HK$',
  'KES': 'KSh',
  'NGN': '₦',
  'GHS': 'GH₵',
  'LKR': 'Rs',
  'NPR': 'Rs',
  'MAD': 'DH',
  'EGP': 'E£',
  'TZS': 'TSh',
  'QAR': 'QR',
  'BDT': '৳',
  'PKR': 'Rs',
  'NZD': 'NZ$',
  'COP': 'COL$',
  'PEN': 'S/'
};

// In-memory cache for hotel data
let hotelDataCache = null;

/**
 * Get hotel currency from in-memory cache or API client
 * @returns {string} Currency code (e.g., 'INR', 'USD')
 */
export const getHotelCurrency = () => {
  // Try to get from in-memory cache first
  if (hotelDataCache && hotelDataCache.currency) {
    return hotelDataCache.currency.toUpperCase();
  }
  
  // Try to get from API client current user
  try {
    // Import ApiClient dynamically to avoid circular dependencies
    const apiClientModule = require('../service/ApiClient');
    const apiClient = apiClientModule.default || apiClientModule;
    
    if (apiClient && apiClient.currentUser) {
      const user = apiClient.currentUser;
      
      // Check user.hotel.currency first (from your API response structure)
      if (user.hotel && user.hotel.currency) {
        const currency = user.hotel.currency.toUpperCase();
        // Cache the hotel data in memory
        hotelDataCache = {
          currency,
          name: user.hotel.name,
          code: user.hotel.code,
          id: user.hotel.id
        };
        return currency;
      }
      
      // Fallback to user_profile.hotel.currency if available
      if (user.user_profile && user.user_profile.hotel && user.user_profile.hotel.currency) {
        const currency = user.user_profile.hotel.currency.toUpperCase();
        hotelDataCache = {
          currency,
          name: user.user_profile.hotel.name,
          code: user.user_profile.hotel.code,
          id: user.user_profile.hotel.id
        };
        return currency;
      }
    }
  } catch (error) {
    console.warn('Error getting currency from API client:', error);
  }
  
  // Fallback to default
  return DEFAULT_CURRENCY;
};

/**
 * Update hotel data cache
 * @param {Object} hotelData - Hotel data object
 */
export const updateHotelDataCache = (hotelData) => {
  if (hotelData) {
    hotelDataCache = {
      currency: hotelData.currency,
      name: hotelData.name,
      code: hotelData.code,
      id: hotelData.id
    };
  }
};

/**
 * Clear hotel data cache
 */
export const clearHotelDataCache = () => {
  hotelDataCache = null;
};

/**
 * Get appropriate locale for currency formatting
 * @param {string} currency - Currency code
 * @returns {string} Locale string
 */
export const getCurrencyLocale = (currency = null) => {
  const currencyCode = currency || getHotelCurrency();
  return CURRENCY_LOCALE_MAP[currencyCode] || DEFAULT_LOCALE;
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code  
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency = null) => {
  const currencyCode = currency || getHotelCurrency();
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

/**
 * Format amount as currency with proper locale and symbol
 * @param {number|string} amount - Amount to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    currency = null,
    locale = null,
    minimumFractionDigits = null,
    maximumFractionDigits = null,
    showSymbol = true,
    compact = false
  } = options;

  const currencyCode = currency || getHotelCurrency();
  const localeCode = locale || getCurrencyLocale(currencyCode);

  if (!amount && amount !== 0) return '-';

  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return '-';

  // 0-decimal currencies (JPY, VND, IDR) must never render trailing ".00" —
  // default to the currency's own decimal-place convention instead of a
  // hardcoded 2, and only widen it if the caller explicitly asked for more.
  const rules = getCurrencyRules(currencyCode);
  const minFractionDigits = minimumFractionDigits !== null ? minimumFractionDigits : 0;
  const maxFractionDigits = maximumFractionDigits !== null
    ? maximumFractionDigits
    : Math.max(rules.decimalPlaces, minFractionDigits);

  try {
    const formatOptions = {
      style: showSymbol ? 'currency' : 'decimal',
      currency: currencyCode,
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits
    };
    
    if (compact) {
      formatOptions.notation = 'compact';
      formatOptions.compactDisplay = 'short';
    }
    
    const formatter = new Intl.NumberFormat(localeCode, formatOptions);
    return formatter.format(numericAmount);
  } catch (error) {
    console.warn('Error formatting currency:', error);
    // Fallback to simple formatting
    const symbol = showSymbol ? getCurrencySymbol(currencyCode) : '';
    return `${symbol}${numericAmount.toLocaleString(localeCode, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits
    })}`;
  }
};

/**
 * Format currency for compact display (e.g., "₹1.2K", "$5.5M")
 * @param {number|string} amount - Amount to format
 * @param {object} options - Formatting options
 * @returns {string} Compact formatted currency
 */
export const formatCurrencyCompact = (amount, options = {}) => {
  return formatCurrency(amount, {
    ...options,
    compact: true,
    maximumFractionDigits: 1
  });
};

/**
 * Format currency without decimals for whole numbers
 * @param {number|string} amount - Amount to format
 * @param {object} options - Formatting options  
 * @returns {string} Formatted currency without decimals
 */
export const formatCurrencyWhole = (amount, options = {}) => {
  return formatCurrency(amount, {
    ...options,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed number or NaN
 */
export const parseCurrency = (currencyString) => {
  if (typeof currencyString !== 'string') {
    return typeof currencyString === 'number' ? currencyString : NaN;
  }
  
  // Remove currency symbols and formatting
  const cleanString = currencyString
    .replace(/[₹$€£¥]/g, '') // Remove common currency symbols
    .replace(/[,\s]/g, '') // Remove commas and spaces
    .trim();
    
  return parseFloat(cleanString);
};

/**
 * Check if currency requires special formatting rules
 * @param {string} currency - Currency code
 * @returns {object} Special formatting rules
 */
export const getCurrencyRules = (currency = null) => {
  const currencyCode = currency || getHotelCurrency();
  
  const rules = {
    'INR': { 
      useIndianNumbering: true, // Lakh, Crore system
      decimalPlaces: 2
    },
    'JPY': { 
      decimalPlaces: 0 // Japanese Yen typically has no decimals
    },
    'USD': { 
      decimalPlaces: 2
    },
    'EUR': { 
      decimalPlaces: 2
    },
    'JOD': { 
      decimalPlaces: 2 // storage is 2dp; see CURRENCY_SYMBOLS note
    },
    'IDR': {
      decimalPlaces: 0 // Rupiah is conventionally shown without decimals
    },
    'MYR': {
      decimalPlaces: 2
    },
    'DOP': {
      decimalPlaces: 2
    },
    'VND': {
      decimalPlaces: 0 // Vietnamese Dong is conventionally shown without decimals
    },
    'LAK': {
      decimalPlaces: 0 // Lao Kip has no subunit in practice
    },
    'TZS': {
      decimalPlaces: 0 // Tanzanian Shilling is conventionally shown without decimals
    },
    'COP': {
      decimalPlaces: 0 // Colombian Peso is conventionally shown without decimals
    }
  };
  
  return rules[currencyCode] || { decimalPlaces: 2 };
};

/**
 * Update hotel currency in memory cache
 * @param {string} newCurrency - New currency code
 */
export const updateHotelCurrency = (newCurrency) => {
  try {
    // Update the in-memory cache
    if (hotelDataCache) {
      hotelDataCache.currency = newCurrency.toUpperCase();
    } else {
      // If no cache exists, create it with minimal data
      hotelDataCache = {
        currency: newCurrency.toUpperCase(),
        name: null,
        code: null,
        id: null
      };
    }
    
  } catch (error) {
    console.error('Error updating hotel currency:', error);
  }
};

export default {
  getHotelCurrency,
  getCurrencyLocale,
  getCurrencySymbol,
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyWhole,
  parseCurrency,
  getCurrencyRules,
  updateHotelCurrency,
  updateHotelDataCache,
  clearHotelDataCache
};